/**
 * The widget's footprint in CSS pixels. Single source for both the widget's own
 * styles and the iframe geometry the host bridge sends — if these drift apart,
 * the iframe clips the widget, so they must come from the same place.
 */
export const WIDGET_GEOMETRY = {
  /** Distance the widget keeps from the viewport corner (ChatbotWrapper margin). */
  margin: 10,
  /** Launcher bubble, and the collapsed chat window. */
  bubbleSize: 64,
  /** Chat window at its largest — it shrinks with the viewport below this. */
  maxWidth: 395,
  maxHeight: 714,
  /** Teaser popup above the bubble. */
  popup: {
    maxWidth: 257,
    /** Distance from the bottom edge — clears the bubble. */
    bottomOffset: 90,
    /** Used until the rendered popup has been measured. */
    fallbackHeight: 170,
  },
  /** Breathing room for the drop shadow, which paints outside the box. */
  shadowPad: 10,
} as const;

/** Which shape the widget currently has on screen. */
export type WidgetShape = "hidden" | "bubble" | "teaser" | "window";

export interface IframeSize {
  width: number;
  height: number;
}

const clamp = (value: number, max: number | undefined): number =>
  max && max > 0 ? Math.min(value, max) : value;

/**
 * The iframe size the current widget shape needs.
 *
 * Note the asymmetry: the chat window sizes itself with `calc(100% - 20px)`, so
 * inside an iframe it can only be as large as we make the iframe — its size is
 * *prescribed* here. The launcher and the teaser have intrinsic sizes instead,
 * so for those the iframe follows the content.
 */
export const computeIframeSize = (
  shape: WidgetShape,
  parent?: Partial<Readonly<{ width: number; height: number }>>,
  measuredPopupHeight?: number,
): IframeSize => {
  const { margin, bubbleSize, maxWidth, maxHeight, popup, shadowPad } =
    WIDGET_GEOMETRY;

  switch (shape) {
    case "hidden":
      // `hideLauncher` with the chat closed: nothing is rendered, so the iframe
      // must not cover the host page either.
      return { width: 0, height: 0 };

    case "bubble":
      return {
        width: bubbleSize + margin,
        height: bubbleSize + margin,
      };

    case "teaser": {
      const popupHeight = measuredPopupHeight ?? popup.fallbackHeight;

      return {
        width: clamp(popup.maxWidth + margin + shadowPad, parent?.width),
        height: clamp(
          popup.bottomOffset + popupHeight + shadowPad,
          parent?.height,
        ),
      };
    }

    case "window":
    default:
      // The window keeps `margin` on every side, so the iframe carries it twice
      // — on a small viewport the iframe simply takes everything it can get.
      return {
        width: clamp(maxWidth + 2 * margin, parent?.width),
        height: clamp(maxHeight + 2 * margin, parent?.height),
      };
  }
};
